import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Theme Context for Dark/Light Mode
 *
 * Follows Ant Design v5 standards:
 * - Uses theme.darkAlgorithm and theme.defaultAlgorithm
 * - Persists theme preference to localStorage
 * - Detects system preference on first load
 * - Provides toggle function for theme switching
 *
 * Usage:
 * import { useTheme } from './contexts/ThemeContext';
 *
 * const { isDarkMode, toggleTheme } = useTheme();
 */

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // 1. Check localStorage first (user preference)
    const savedTheme = localStorage.getItem('dockal-registry-theme');
    if (savedTheme !== null) {
      return savedTheme === 'dark';
    }

    // 2. Fall back to system preference
    if (window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    // 3. Default to light mode
    return false;
  });

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('dockal-registry-theme');
      if (savedTheme === null) {
        setIsDarkMode(e.matches);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Persist theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dockal-registry-theme', isDarkMode ? 'dark' : 'light');

    // Optional: Update document class for CSS styling
    document.documentElement.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const setTheme = (isDark: boolean) => {
    setIsDarkMode(isDark);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

export default ThemeContext;
